import { useMemo } from 'react';
import { getMessages, MessagesPage, RequestOptions } from '@connect-x-app/hybrid-data-access';
import { ApiError, AuthenticatedUser, Message, QueryKeys } from '@connect-x-app/shared-domain-models';
import { DEFAULT_PAGE_SIZE } from '@connect-x-app/shared-utils';
import { InfiniteData, QueryKey, useInfiniteQuery } from '@tanstack/react-query';

import { useMessageStore } from '../../stores';

type MessageProps = {
  anchorId: string;
  direction: 'after' | 'before';
  include?: boolean;
};

// Pagination anchors are resolved from the message store rather than from the last fetched page.
// The store merges everything we have loaded (fetched pages, socket events, gap fills) and keeps it
// sorted newest-first, so its first/last entries are the true edges of what we already have. Anchoring
// on those edges means we always continue from where the store actually ends and never re-request
// messages we already hold. Optimistic messages carry an empty id until the server confirms them
// (see mapStringToMessage), and an empty id cannot anchor a v4 request, so they are skipped.
const resolveAnchorId = (messages: Message[], edge: 'newest' | 'oldest'): string | undefined => {
  const hasServerId = (message: Message) => message.id !== '';
  const orderedFromEdge = edge === 'newest' ? messages : [...messages].reverse();
  return orderedFromEdge.find(hasServerId)?.id;
};

export const useGetPaginatedMessages = ({
  channelId,
  flightRoomId,
  messageId,
  user,
}: {
  channelId?: string | null;
  flightRoomId?: string | null;
  messageId?: string;
  user: AuthenticatedUser | null;
}) => {
  const limit = DEFAULT_PAGE_SIZE;
  const options: RequestOptions<Message> = { limit };

  const { messagesMap } = useMessageStore();

  const channelMessages = useMemo(
    () => (channelId ? (messagesMap.get(channelId)?.flatMap(chunk => [...chunk.values()]) ?? []) : []),
    [channelId, messagesMap],
  );

  return useInfiniteQuery<MessagesPage, ApiError, InfiniteData<MessagesPage>, QueryKey, MessageProps | undefined>({
    enabled: user !== null && !!channelId,
    gcTime: 0,
    getNextPageParam: (result, _allPages, lastPageParam) => {
      if (!result?.items?.length) {
        return undefined;
      }

      // Only treat a partial page as the last page when the page was actually fetched in the
      // 'after' direction. The initial page is fetched without a direction (API defaults to
      // 'before'), so its item count says nothing about how many messages exist after the anchor.
      if (lastPageParam?.direction === 'after' && result.items.length < limit) {
        return undefined;
      }

      const anchorId = resolveAnchorId(channelMessages, 'newest');
      if (!anchorId) {
        return undefined;
      }

      return { anchorId, direction: 'after', include: false };
    },
    getPreviousPageParam: (result, _allPages, firstPageParam) => {
      if (!result?.items?.length) {
        return undefined;
      }

      // The initial page (firstPageParam undefined) is fetched with the API's default 'before'
      // direction, so a partial result correctly signals there are no more older messages.
      // Explicit 'before' fetches are treated the same way.
      if (firstPageParam?.direction !== 'after' && result.items.length < limit) {
        return undefined;
      }

      const anchorId = resolveAnchorId(channelMessages, 'oldest');
      if (!anchorId) {
        return undefined;
      }

      return { anchorId, direction: 'before', include: false };
    },
    initialPageParam: undefined,
    queryFn: context => {
      const anchorId = context.pageParam?.anchorId ?? messageId;
      const direction = context.pageParam?.direction;
      const include = context.pageParam !== undefined ? context.pageParam.include : !!messageId;
      return getMessages(flightRoomId!, channelId!, options, anchorId, direction, include);
    },
    queryKey: [QueryKeys.MESSAGES, channelId, messageId],
    // we want this data to always be considered stale so it is refetched in de background
    staleTime: 0,
  });
};
